import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from "react";
import {
  login as apiLogin,
  authApi,
  authUrl,
  ApiError,
  UNAUTHENTICATED_EVENT,
  type AuthConfig,
  type TenantInfo,
} from "../lib/api";

/**
 * Session state for the dashboard.
 *
 * The session itself is an httpOnly cookie on the API origin (set by the Titanium
 * hosted login): this context only asks the API who is signed in
 * (GET /auth/session). Nothing about the session is kept in localStorage.
 */

interface AuthContextValue {
  tenant: TenantInfo | null;
  isLoading: boolean;
  config: AuthConfig;
  /** Re-read the session from the API (after a legacy login or setup). */
  refresh: () => Promise<void>;
  /** LEGACY password login (only when the API has LEGACY_PASSWORD_LOGIN=on). */
  legacyLogin: (email: string, password: string) => Promise<void>;
  /** Sign out here AND of Titanium (top-level navigation through the API). */
  logout: () => void;
}

const DEFAULT_CONFIG: AuthConfig = { legacyPasswordLogin: false, openSignup: false };

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Top-level form POST to the API's /auth/logout: it clears the session cookie and
 * continues to the Titanium portal logout (a fetch could not end the portal
 * session, whose cookies are SameSite=Lax on another site).
 */
export function submitLogout(): void {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = authUrl("/auth/logout");
  document.body.appendChild(form);
  form.submit();
}

export function AuthProvider({
  children,
  initialTenant,
}: {
  children: ReactNode;
  /** Storybook / tests: start signed in without asking the API. */
  initialTenant?: TenantInfo;
}) {
  const [tenant, setTenant] = useState<TenantInfo | null>(initialTenant ?? null);
  const [config, setConfig] = useState<AuthConfig>(DEFAULT_CONFIG);
  const [isLoading, setIsLoading] = useState(!initialTenant);

  const refresh = useCallback(async () => {
    try {
      const session = await authApi.session();
      setTenant(session?.tenant ?? null);
    } catch {
      setTenant(null);
    }
  }, []);

  useEffect(() => {
    // Stale tokens from the pre-Titanium bearer login: no longer used, remove them.
    try {
      localStorage.removeItem("token");
      localStorage.removeItem("tenant");
    } catch {}

    if (initialTenant) return;
    let cancelled = false;
    void (async () => {
      const [, cfg] = await Promise.all([refresh(), authApi.config().catch(() => DEFAULT_CONFIG)]);
      if (!cancelled) {
        setConfig(cfg);
        setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [initialTenant, refresh]);

  // Any API 401 means the cookie session is gone (expired, signed out elsewhere).
  useEffect(() => {
    if (initialTenant) return;
    const onUnauthenticated = () => setTenant(null);
    window.addEventListener(UNAUTHENTICATED_EVENT, onUnauthenticated);
    return () => window.removeEventListener(UNAUTHENTICATED_EVENT, onUnauthenticated);
  }, [initialTenant]);

  async function legacyLogin(email: string, password: string) {
    const res = await apiLogin(email, password);
    setTenant(res.tenant);
  }

  function logout() {
    setTenant(null);
    submitLogout();
  }

  return (
    <AuthContext.Provider value={{ tenant, isLoading, config, refresh, legacyLogin, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export { ApiError };
